import React, { useState, useEffect, useRef } from 'react';
import { HouseIcon, CompassIcon, User as UserIcon, FolderIcon, GearIcon, CaretLeftIcon, CaretRightIcon, Image as ImageIcon, VideoIcon, GlobeIcon, BookOpenIcon, LockIcon, SignInIcon, SignOutIcon, ArrowsLeftRightIcon, SparkleIcon, LayoutIcon, GridFourIcon, MonitorPlayIcon, FilmSlateIcon, EyeIcon, PresentationIcon, Lightbulb as LightbulbIcon, MapPinArea as MapPinAreaIcon, PencilSimple as PencilSimpleIcon } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { useProject } from '../context/ProjectContext';
import { NotificationsDropdown } from './NotificationsDropdown';
import { ThemeSwitcher } from './ThemeSwitcher';

interface SidebarProps {
  currentView?: string;
  onNavigate?: (view: string) => void;
  isOpen: boolean;
  onToggle: () => void;
  side?: 'left' | 'right';
  onSwapSides?: () => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  currentView = 'studio',
  onNavigate = (view: string) => { },
  isOpen,
  onToggle,
  side = 'left',
  onSwapSides,
  mobileOpen = false,
  onMobileClose
}) => {
  const { user, openAuthModal, logout, creditBalance } = useAuth();
  const { selectedProjectId } = useProject();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigationRef = useRef<HTMLDivElement>(null);
  const expanded = isOpen || mobileOpen;
  const navigate = (view: string) => { onMobileClose?.(); onNavigate(view); };
  useEffect(() => {
    if (!mobileOpen) return;
    const navigation = navigationRef.current;
    navigation?.querySelector<HTMLButtonElement>('button')?.focus();
    const keyboard = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); onMobileClose?.(); }
      if (event.key !== 'Tab') return;
      const controls = Array.from(navigation?.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], input:not(:disabled), select') ?? [])
        .filter(element => element.getClientRects().length > 0);
      const first = controls[0], last = controls.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', keyboard);
    return () => {
      document.removeEventListener('keydown', keyboard);
      const toggle = document.getElementById('studio-navigation-toggle');
      if (toggle?.getClientRects().length) toggle.focus();
    };
  }, [mobileOpen, onMobileClose]);

  // State for showing toggle handle on edge hover (when collapsed)
  const [isHandleVisible, setIsHandleVisible] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      {mobileOpen && <button aria-label="Close navigation" onClick={onMobileClose}
        className="fixed inset-0 z-[80] bg-black/60 lg:hidden" />}
      {/* Edge hover zone - reveals toggle handle when collapsed */}
      {!isOpen && (
        <div
          className={`fixed top-0 h-screen w-6 z-40 hidden lg:block ${side === 'left' ? 'left-0' : 'right-0'}`}
          onMouseEnter={() => setIsHandleVisible(true)}
          onMouseLeave={() => setIsHandleVisible(false)}
        />
      )}
      <div
        id="studio-navigation" ref={navigationRef} role="navigation" aria-label="Main navigation"
        className={`
        ${mobileOpen ? 'flex w-[240px] z-[90]' : 'hidden w-0 z-40'} lg:flex flex-col fixed top-0 h-screen bg-onyx backdrop-blur-xl
        transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]
        ${side === 'left' ? 'left-0' : 'right-0'}
        ${isOpen ? 'lg:w-[240px]' : 'lg:w-0'}
        ${expanded && (side === 'left' ? 'border-r border-white/5' : 'border-l border-white/5')}
      `}
      >

        {/* Content Container - fades out when collapsed */}
        <div aria-hidden={!expanded} inert={!expanded ? true : undefined} className={`flex flex-col h-full w-[240px] transition-opacity duration-200 ${expanded ? 'opacity-100 delay-100' : 'opacity-0 pointer-events-none'} relative z-10`}>

          {/* Header */}
          <div className="h-20 flex items-center justify-between px-6 flex-shrink-0">
            {mobileOpen && <button aria-label="Close menu" onClick={onMobileClose} className="lg:hidden mr-2 text-zinc-300 text-xl">×</button>}
            <span className="font-heading font-bold text-2xl text-white tracking-tight cursor-pointer" onClick={() => navigate('studio')}>
              EZ Art.
              {user?.subscription_tier === 'starter' && <span className="ml-2 px-1.5 py-0.5 rounded bg-blue-500/10 text-[10px] text-blue-400 font-mono font-bold">STARTER</span>}
              {user?.subscription_tier === 'pro' && <span className="ml-2 px-1.5 py-0.5 rounded bg-yellow-500/10 text-[10px] text-yellow-500 font-mono font-bold">PRO</span>}
            </span>
            <div className="flex items-center gap-1">
              {user && <NotificationsDropdown />}
              {/* Swap Side Button - Only show when on Right side */}
              {onSwapSides && side === 'right' && (
                <button
                  onClick={onSwapSides}
                  className="p-2 text-zinc-600 hover:text-white hover:bg-white/5 rounded-full transition-colors"
                  title="Swap Sidebar Position"
                >
                  <ArrowsLeftRightIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Top Section */}
          <div className="p-4 mb-2 flex flex-col gap-2">
            {/* Logo Area */}


            {/* Primary Navigation */}
            <div className="space-y-1">
              <NavItem icon={HouseIcon} label="Studio" active={currentView === 'home' || currentView === 'studio' || !currentView} onClick={() => navigate('studio')} />
              <NavItem icon={CompassIcon} label="Discover" active={currentView === 'discover'} onClick={() => navigate('discover')} />
              <NavItem icon={FolderIcon} label="Projects" active={currentView === 'projects'} onClick={() => navigate('projects')} />
            </div>
          </div>

          {/* Separator - only show if project selected */}
          {selectedProjectId && <div className="h-px bg-white/5 mx-4 mb-4" />}

          {/* Project Tools - Only show when a project is selected */}
          {selectedProjectId ? (
            <div className="flex-1 px-4 overflow-y-auto min-h-0 container-snap overflow-x-hidden no-scrollbar">
              {isOpen && (
                <div className="text-[10px] font-bold text-zinc-600 uppercase tracking-widest mb-3 px-2">
                  Production
                </div>
              )}
              <div className="space-y-1 pb-4">
                <NavItem icon={LightbulbIcon} label="Idea" active={currentView === 'idea'} onClick={() => navigate('idea')} locked={!user} />
                <NavItem icon={BookOpenIcon} label="Story" active={currentView === 'story'} onClick={() => navigate('story')} locked={!user} />
                <NavItem icon={MapPinAreaIcon} label="Atlas" active={currentView === 'atlas'} onClick={() => navigate('atlas')} locked={!user} />
                <NavItem icon={UserIcon} label="Character" active={currentView === 'character'} onClick={() => navigate('character')} locked={!user} />
                <NavItem icon={EyeIcon} label="Lookbook" active={currentView === 'lookbook'} onClick={() => navigate('lookbook')} locked={!user} />
                <NavItem icon={PencilSimpleIcon} label="Doodle" active={currentView === 'doodle'} onClick={() => navigate('doodle')} locked={!user} />
                <NavItem icon={PresentationIcon} label="Storyboard" active={currentView === 'storyboard'} onClick={() => navigate('storyboard')} locked={!user} />
                <NavItem icon={FilmSlateIcon} label="Shot Designer" active={currentView === 'shot-designer'} onClick={() => navigate('shot-designer')} locked={!user} />
                {/* <NavItem icon={GlobeIcon} label="World-Building" active={currentView === 'world'} onClick={() => navigate('world')} locked={true} badge="SOON" /> */}
                {/* <NavItem icon={MonitorPlayIcon} label="Pitch Deck" active={currentView === 'pitch'} onClick={() => navigate('pitch')} locked={false} badge="NEW" /> */}
              </div>
            </div>
          ) : (
            /* Spacer to push footer to bottom when no project selected */
            <div className="flex-1" />
          )}
          {/* Footer Area */}
          <div className="p-4 flex-shrink-0 flex flex-col gap-3">
            {user ? (
              <>
                {/* Profile + Credits */}
                <div className="relative" ref={menuRef}>
                  {/* Popover Menu */}
                  {showProfileMenu && (
                    <div className="absolute bottom-full left-0 w-full mb-2 bg-zinc-900 border border-white/10 rounded-xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-1 z-20">
                      <button
                        onClick={() => {
                          navigate('profile');
                          setShowProfileMenu(false);
                        }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-xs font-medium text-zinc-300 hover:text-white hover:bg-white/5 transition-colors"
                      >
                        <UserIcon className="w-3.5 h-3.5" />
                        My Profile
                      </button>
                      <button
                        onClick={() => {
                          navigate('settings');
                          setShowProfileMenu(false);
                        }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-xs font-medium text-zinc-300 hover:text-white hover:bg-white/5 transition-colors"
                      >
                        <GearIcon className="w-3.5 h-3.5" />
                        Settings
                      </button>
                      <div className="px-3 pb-2.5 pt-1">
                        <ThemeSwitcher variant="compact" />
                      </div>
                      <div className="h-px bg-white/5" />
                      <button
                        onClick={() => {
                          logout();
                          setShowProfileMenu(false);
                        }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-xs font-medium text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                      >
                        <SignOutIcon className="w-3.5 h-3.5" />
                        Sign Out
                      </button>
                    </div>
                  )}

                  <button
                    onClick={() => setShowProfileMenu(!showProfileMenu)}
                    className={`
                      w-full flex items-center gap-3 px-2 py-2 rounded-xl transition-all border border-transparent
                      ${showProfileMenu ? 'bg-graphite border-white/5' : 'hover:bg-graphite'}
                    `}
                  >
                    <div className="w-9 h-9 rounded-full bg-zinc-800 border border-white/10 overflow-hidden relative flex items-center justify-center shrink-0">
                      {user.avatar ? (
                        <img src={user.avatar} alt="User" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-xs font-bold text-zinc-400">
                          {(user.name || user.username)?.[0]?.toUpperCase() || '?'}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <div className="text-xs font-medium text-white truncate">{user.name || user.username}</div>
                      <div className="text-[11px] text-zinc-500 truncate">{creditBalance} credits</div>
                    </div>
                  </button>
                </div>

                {/* Upgrade / Buy Credits button */}
                {(!user.subscription_tier || user.subscription_tier === 'free') ? (
                  <button
                    onClick={() => navigate('pricing')}
                    className="w-full py-2 text-xs font-semibold text-white rounded-lg border border-white/10 hover:bg-white/5 transition-colors"
                  >
                    Upgrade to Pro
                  </button>
                ) : (
                  <button
                    onClick={() => navigate('pricing')}
                    className="w-full py-2 text-xs font-semibold text-white rounded-lg border border-white/10 hover:bg-white/5 transition-colors"
                  >
                    Buy Credits
                  </button>
                )}
              </>
            ) : (
              <button
                onClick={() => openAuthModal()}
                className="w-full py-2.5 flex items-center justify-center gap-2 bg-white text-black rounded-lg text-sm font-bold hover:bg-zinc-200 transition-colors"
              >
                <SignInIcon className="w-4 h-4" />
                Sign In
              </button>
            )}
          </div>
        </div>

      </div>

      {/* Sleek Toggle Handle - Flush with sidebar, hidden when collapsed until hover */}
      <button
        onClick={onToggle}
        onMouseEnter={() => !isOpen && setIsHandleVisible(true)}
        onMouseLeave={() => !isOpen && setIsHandleVisible(false)}
        className={`
          fixed top-0 bottom-0 cursor-pointer flex items-center justify-center group z-50 outline-none
          transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]
          ${isOpen
            ? 'w-[2px] bg-white/5 hover:w-1 hover:bg-white/10'
            : isHandleVisible
              ? 'w-6 bg-onyx/95 hover:bg-zinc-900 backdrop-blur-xl'
              : 'w-8 bg-onyx/95 border border-white/10'}
          ${side === 'left'
            ? `${isOpen ? 'left-[240px]' : 'left-0'} ${!isOpen && isHandleVisible && 'border-r border-white/5'}`
            : `${isOpen ? 'right-[240px]' : 'right-0'} ${!isOpen && isHandleVisible && 'border-l border-white/5'}`}
          hidden lg:flex
      `}
        title={isOpen ? "Collapse Sidebar" : "Expand Sidebar"}
      >
        {/* Chevron Indicator - Only visible when collapsed and handle is shown */}
        {!isOpen && (
          <div className="opacity-40 group-hover:opacity-100 transition-opacity">
            {side === 'left' ? (
              <CaretRightIcon className="w-3 h-3 text-white" />
            ) : (
              <CaretLeftIcon className="w-3 h-3 text-white" />
            )}
          </div>
        )}
      </button>

    </>
  );
};

const NavItem = ({ icon: Icon, label, href, active = false, locked = false, badge, onClick }: { icon: any, label: string, href?: string, active?: boolean, locked?: boolean, badge?: string, onClick?: (e: React.MouseEvent) => void }) => {
  const Component = href ? 'a' : 'button';

  return (
    <Component
      href={href}
      onClick={onClick}
      className={`
        w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-all duration-300 group
        ${active
          ? 'bg-graphite text-white font-medium'
          : 'bg-transparent border-transparent text-zinc-500 hover:text-white hover:bg-graphite'}
        ${locked ? 'opacity-60 hover:opacity-100 cursor-not-allowed' : ''}
      `}
    >
      <div className="flex items-center gap-3">
        <Icon className={`w-4 h-4 ${active ? 'text-white' : 'text-zinc-500 group-hover:text-white'}`} />
        <span className="whitespace-nowrap">{label}</span>
      </div>
      {badge ? (
        <span className="px-1.5 py-0.5 text-[9px] font-bold bg-zinc-800 text-zinc-400 rounded-md border border-white/10 tracking-wider font-mono">{badge}</span>
      ) : (
        locked && <LockIcon className="w-3 h-3 text-zinc-600" />
      )}
    </Component>
  );
};
